import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  UserCircleIcon,
  PhotoIcon,
  DocumentIcon,
} from '@heroicons/react/24/outline';
import clientService from '../../services/clientService';
import agencyService from '../../services/agencyService';
import { useToast } from '../../contexts/ToastContext';

const ClientForm = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const validationSchema = Yup.object({
    first_name: Yup.string()
      .required('Le prénom est requis')
      .min(2, 'Le prénom doit contenir au moins 2 caractères'),
    last_name: Yup.string()
      .required('Le nom est requis')
      .min(2, 'Le nom doit contenir au moins 2 caractères'),
    gender: Yup.string()
      .required('Le genre est requis'),
    date_of_birth: Yup.date()
      .required('La date de naissance est requise')
      .max(new Date(), 'La date ne peut pas être dans le futur'),
    national_id: Yup.string()
      .required('La pièce d\'identité est requise'),
    phone: Yup.string()
      .required('Le téléphone est requis')
      .matches(/^[0-9]+$/, 'Numéro de téléphone invalide')
      .min(9, 'Le téléphone doit contenir au moins 9 chiffres'),
    email: Yup.string()
      .email('Email invalide')
      .nullable(),
    address: Yup.string()
      .required('L\'adresse est requise'),
    occupation: Yup.string()
      .required('La profession est requise'),
    monthly_income: Yup.number()
      .required('Le revenu mensuel est requis')
      .min(0, 'Le revenu ne peut pas être négatif'),
    agency_id: Yup.string()
      .required('L\'agence est requise'),
  });

  const formik = useFormik({
    initialValues: {
      first_name: '',
      last_name: '',
      gender: '',
      date_of_birth: '',
      national_id: '',
      phone: '',
      email: '',
      address: '',
      occupation: '',
      monthly_income: '',
      agency_id: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        
        const formattedValues = {
          ...values,
          monthly_income: parseFloat(values.monthly_income),
        };

        if (isEditMode) {
          await clientService.updateClient(id, formattedValues);
          showSuccess('Client mis à jour avec succès');
        } else {
          await clientService.createClient(formattedValues);
          showSuccess('Client créé avec succès');
        }
        
        navigate('/clients');
      } catch (error) {
        showError(error.response?.data?.message || 'Une erreur est survenue');
      } finally {
        setLoading(false);
      }
    },
  });
  useEffect(() => {
    fetchAgencies();
  }, []);

  useEffect(() => {
    if (isEditMode) {
      fetchClient();
    }
  }, [id]);
  const fetchAgencies = async () => {
    try {
      setLoading(true);
      const data = await agencyService.getAllAgencies();
      setAgencies(data);
    } catch (error) {
      showError('Erreur lors du chargement des agences');
    } finally {
      setLoading(false);
    }
  };

  const fetchClient = async () => {
    try {
      setLoading(true);
      const data = await clientService.getClient(id);
      setClientData(data);
      
      // Set form values
      Object.keys(data).forEach(key => {
        if (formik.values[key] !== undefined) {
          formik.setFieldValue(key, data[key]);
        }
      });
    } catch (error) {
      showError('Erreur lors du chargement du client');
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/clients');
  };

  if (loading && isEditMode && !clientData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-2 text-sm text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {isEditMode ? 'Modifier le Client' : 'Nouveau Client'}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {isEditMode
              ? 'Mettez à jour les informations du client'
              : 'Ajoutez un nouveau client au système'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form Section */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="p-6">
              <form onSubmit={formik.handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {/* First Name */}
                  <div>
                    <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                      Prénom *
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        id="first_name"
                        name="first_name"
                        value={formik.values.first_name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="input-field"
                      />
                      {formik.touched.first_name && formik.errors.first_name && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.first_name}</p>
                      )}
                    </div>
                  </div>

                  {/* Last Name */}
                  <div>
                    <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                      Nom *
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        id="last_name"
                        name="last_name"
                        value={formik.values.last_name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="input-field"
                      />
                      {formik.touched.last_name && formik.errors.last_name && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.last_name}</p>
                      )}
                    </div>
                  </div>

                  {/* Gender */}
                  <div>
                    <label htmlFor="gender" className="block text-sm font-medium text-gray-700">
                      Genre *
                    </label>
                    <div className="mt-1">
                      <select
                        id="gender"
                        name="gender"
                        value={formik.values.gender}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="input-field"
                      >
                        <option value="">Sélectionnez</option>
                        <option value="male">Homme</option>
                        <option value="female">Femme</option>
                        <option value="other">Autre</option>
                      </select>
                      {formik.touched.gender && formik.errors.gender && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.gender}</p>
                      )}
                    </div>
                  </div>

                  {/* Date of Birth */}
                  <div>
                    <label htmlFor="date_of_birth" className="block text-sm font-medium text-gray-700">
                      Date de naissance *
                    </label>
                    <div className="mt-1">
                      <input
                        type="date"
                        id="date_of_birth"
                        name="date_of_birth"
                        value={formik.values.date_of_birth}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="input-field"
                      />
                      {formik.touched.date_of_birth && formik.errors.date_of_birth && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.date_of_birth}</p>
                      )}
                    </div>
                  </div>

                  {/* National ID */}
                  <div>
                    <label htmlFor="national_id" className="block text-sm font-medium text-gray-700">
                      Pièce d'identité *
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        id="national_id"
                        name="national_id"
                        value={formik.values.national_id}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="input-field"
                        placeholder="Numéro CNI/Passeport"
                      />
                      {formik.touched.national_id && formik.errors.national_id && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.national_id}</p>
                      )}
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                      Téléphone *
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        id="phone"
                        name="phone"
                        value={formik.values.phone}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="input-field"
                        placeholder="Ex: 771234567"
                      />
                      {formik.touched.phone && formik.errors.phone && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.phone}</p>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <div className="mt-1">
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="input-field"
                        placeholder="email@exemple.com"
                      />
                      {formik.touched.email && formik.errors.email && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.email}</p>
                      )}
                    </div>
                  </div>

                  {/* Occupation */}
                  <div>
                    <label htmlFor="occupation" className="block text-sm font-medium text-gray-700">
                      Profession *
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        id="occupation"
                        name="occupation"
                        value={formik.values.occupation}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="input-field"
                        placeholder="Ex: Commerçant, Agriculteur"
                      />
                      {formik.touched.occupation && formik.errors.occupation && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.occupation}</p>
                      )}
                    </div>
                  </div>

                  {/* Monthly Income */}
                  <div>
                    <label htmlFor="monthly_income" className="block text-sm font-medium text-gray-700">
                      Revenu mensuel (HTG) *
                    </label>
                    <div className="mt-1">
                      <input
                        type="number"
                        id="monthly_income"
                        name="monthly_income"
                        value={formik.values.monthly_income}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="input-field"
                        placeholder="0"
                        min="0"
                        step="1000"
                      />
                      {formik.touched.monthly_income && formik.errors.monthly_income && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.monthly_income}</p>
                      )}
                    </div>
                  </div>

                  {/* Agency */}
                  <div>
                    <label htmlFor="agency_id" className="block text-sm font-medium text-gray-700">
                      Agence *
                    </label>
                    <div className="mt-1">
                      <select
                        id="agency_id"
                        name="agency_id"
                        value={formik.values.agency_id}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="input-field"
                      >
                      <option value="">Sélectionnez une agence</option>
                      {agencies.map((agency) => (
                        <option key={agency.id} value={agency.id}>
                          {agency.name}
                        </option>
                      ))}
                      </select>
                      {formik.touched.agency_id && formik.errors.agency_id && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.agency_id}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                    Adresse *
                  </label>
                  <div className="mt-1">
                    <textarea
                      id="address"
                      name="address"
                      rows={3}
                      value={formik.values.address}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="input-field"
                      placeholder="Adresse complète"
                    />
                    {formik.touched.address && formik.errors.address && (
                      <p className="mt-2 text-sm text-red-600">{formik.errors.address}</p>
                    )}
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="btn-secondary"
                    disabled={loading}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={loading || !formik.isValid}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {isEditMode ? 'Mise à jour...' : 'Création...'}
                      </>
                    ) : (
                      isEditMode ? 'Mettre à jour' : 'Créer le client'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="space-y-6">
            {/* Photo Upload */}
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Photo du client</h3>
                <div className="flex items-center justify-center">
                  <div className="relative">
                    <div className="h-32 w-32 rounded-full bg-gray-100 border-4 border-white shadow-lg flex items-center justify-center">
                      <UserCircleIcon className="h-24 w-24 text-gray-300" />
                    </div>
                    <button
                      type="button"
                      className="absolute bottom-0 right-0 h-8 w-8 rounded-full bg-primary-600 text-white flex items-center justify-center hover:bg-primary-700"
                    >
                      <PhotoIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <p className="mt-4 text-sm text-gray-600 text-center">
                  Cliquez sur l'icône pour ajouter une photo
                </p>
              </div>
            </div>

            {/* Documents */}
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Documents</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <DocumentIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Pièce d'identité</p>
                        <p className="text-xs text-gray-500">CNI / Passeport</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="text-sm text-primary-600 hover:text-primary-900"
                    >
                      Télécharger
                    </button>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <DocumentIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Justificatif de domicile</p>
                        <p className="text-xs text-gray-500">Facture récente</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="text-sm text-primary-600 hover:text-primary-900"
                    >
                      Télécharger
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Information */}
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Informations</h3>
                <ul className="space-y-3">
                  <li className="flex justify-between">
                    <span className="text-sm text-gray-600">Client ID</span>
                    <span className="text-sm font-medium text-gray-900">
                      {clientData?.client_number || 'Généré automatiquement'}
                    </span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-sm text-gray-600">Date d'inscription</span>
                    <span className="text-sm font-medium text-gray-900">
                      {clientData?.created_at 
                        ? new Date(clientData.created_at).toLocaleDateString('fr-FR')
                        : new Date().toLocaleDateString('fr-FR')}
                    </span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-sm text-gray-600">Statut</span>
                    <span className="badge badge-success">
                      {clientData?.status || 'Actif'}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientForm;